import React from 'react';
import SolutionCard, { SolutionCardProps } from './SolutionCard';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Filter, Plus } from 'lucide-react';

interface SolutionGridProps {
  solutions: SolutionCardProps[];
  className?: string;
  isLoading?: boolean;
  selectedCategory?: string | null;
  categoryName?: string;
}

const SolutionGrid = ({ 
  solutions, 
  className, 
  isLoading = false,
  selectedCategory,
  categoryName
}: SolutionGridProps) => {
  if (isLoading) {
    return (
      <div className={cn(
        "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6",
        className
      )}>
        {[...Array(6)].map((_, i) => (
          <div key={i} className="flex flex-col h-full">
            <Skeleton className="aspect-video w-full rounded-t-xl" />
            <div className="space-y-3 p-4">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-7 w-3/4" />
              <Skeleton className="h-20 w-full" />
              <div className="flex gap-2 pt-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            </div>
            <Skeleton className="h-12 w-full mt-auto rounded-b-xl" />
          </div>
        ))}
      </div>
    );
  }

  if (solutions.length === 0) {
    const isFiltered = selectedCategory !== null;
    
    return (
      <div className="text-center py-16">
        <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-6">
          {isFiltered ? (
            <Filter className="w-10 h-10 text-muted-foreground" />
          ) : (
            <Search className="w-10 h-10 text-muted-foreground" />
          )}
        </div>
        
        {isFiltered ? (
          <>
            <h3 className="text-xl font-medium mb-2">No solutions found in {categoryName || 'this category'}</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              We don't have any AI solutions in the {categoryName || 'selected'} category yet. 
              Try exploring other categories or check back later for new additions.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-muted transition-colors"
              >
                ← Go Back
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-xl font-medium mb-2">No solutions available</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              It looks like there are no AI solutions available at the moment. 
              Please check back later.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-muted transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Solutions grid */}
      <div className={cn(
        "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      )}>
        {solutions.map((solution, index) => (
          <div 
            key={solution.id || index}
            className="animate-fade-up opacity-0"
            style={{ 
              animationDelay: `${0.1 + (index % 10) * 0.05}s`,
              animationFillMode: 'forwards' 
            }}
          >
            <SolutionCard {...solution} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default SolutionGrid;
